import { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import { useFormContext } from 'react-hook-form';

const DestinationMap = (props) => {
  const methods = useFormContext();
  const { setValue, watch } = methods;

  const mapRef = useRef(null);
  const { handleMaps, waypoint, desi, qPit } = props;

  const [source, setSource] = useState(null);
  const [destination, setDestination] = useState(null);
  const [map, setMap] = useState(null);
  const [service, setService] = useState(null);
  const [renderer, setRenderer] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const latitude = watch('l_latitude');
  const longitude = watch('l_longitude');

  useEffect(() => {
    if (
      latitude === undefined ||
      latitude === null ||
      latitude === '' ||
      longitude === undefined ||
      longitude === null ||
      longitude === ''
    ) {
      setSearchValue('');
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    // Default form values are 0,0 — keep the search field empty until real coords exist
    if (Number.isNaN(lat) || Number.isNaN(lng) || (lat === 0 && lng === 0)) {
      setSearchValue('');
      return;
    }

    setSearchValue(`${latitude}, ${longitude}`);
  }, [latitude, longitude]);

  useEffect(() => {
    setService(new window.google.maps.DirectionsService());
    setRenderer(new window.google.maps.DirectionsRenderer());
  }, []);

  useEffect(() => {
    if (qPit && map) {
      console.log(`updating source from prop`, qPit);
      const _m = new window.google.maps.Marker({
        position: {
          lat: qPit?.lat ? Number(qPit.lat) : 30.2426319,
          lng: qPit?.lng ? Number(qPit.lng) : -97.7542349,
        },
        map,
      });
      setSource(_m);
    }
  }, [qPit, map]);

  useEffect(() => {
    if (desi && map) {
      console.log(`updating destination from prop`, desi);
      const lat = desi?.latLng?.lat ? Number(desi.latLng.lat) : null;
      const lng = desi?.latLng?.lng ? Number(desi.latLng.lng) : null;
      if (lat != null && lng != null && !(lat === 0 && lng === 0)) {
        setDestinationMarker(lat, lng);
        map.setCenter({ lat, lng });
        map.panTo({ lat, lng });
      }
    }
  }, [desi, map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    if (
      latitude === undefined ||
      latitude === null ||
      latitude === '' ||
      longitude === undefined ||
      longitude === null ||
      longitude === '' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng) ||
      (lat === 0 && lng === 0)
    ) {
      return;
    }

    setDestinationMarker(lat, lng);
    map.setCenter({ lat, lng });
    map.panTo({ lat, lng });
  }, [latitude, longitude, map]);

  const checkLongName = (objData, id) => {
    if (
      objData &&
      objData.address_components &&
      objData.address_components.length &&
      objData.address_components[id] != undefined &&
      objData.address_components.length == id &&
      objData.address_components[id].long_name
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onMapClicked = (event) => {
    console.log(event.latLng.lat(), event.latLng.lng());

    setDestinationMarker(event.latLng.lat(), event.latLng.lng());

    setValue('l_latitude', event.latLng.lat());
    setValue('l_longitude', event.latLng.lng());

    var geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ latLng: event.latLng }, (results, status) => {
      if (status == google.maps.GeocoderStatus.OK) {
        let object = results[0];
        handleAddressChange(object, { lat: event.latLng.lat(), lng: event.latLng.lng() });
      }
    });
  };

  useEffect(() => {
    if (!mapRef.current) return;

    const _map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: 30.2426319, lng: -97.7542349 },
    });

    _map.addListener('click', onMapClicked);
    setMap(_map);
  }, [mapRef]);

  useEffect(() => {
    if (map) {
      renderer.setMap(map);
    }
  }, [map]);

  const setDestinationMarker = (lat, lng) => {
    if (destination) destination.setMap(null);

    const marker = new window.google.maps.Marker({
      position: {
        lat,
        lng,
      },
      map,
    });
    setDestination(marker);
  };

  useEffect(() => {
    if (source && destination && map) {
      source.setMap(null);
      destination.setMap(null);
      service
        .route({
          origin: source.getPosition(),
          destination: destination.getPosition(),
          travelMode: window.google.maps.TravelMode.DRIVING,
          // waypoints: waypoint && waypoint.length > 0 ? waypoint : [],
          avoidTolls: true,
          // optimizeWaypoints: true,
        })
        .then((result) => {
          renderer.setDirections(result);
        });
    }
  }, [source, destination, map]);

  const search = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    const input = document.getElementById('autocomplete-input');

    // Replace en-dash and em-dash with regular minus sign and remove extra spaces
    const normalizedValue = value.trim().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, '');
    const coordMatch = normalizedValue.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)$/);

    if (coordMatch && map) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      const position = { lat, lng };

      if (destination) destination.setMap(null);

      const marker = new window.google.maps.Marker({
        position,
        map,
      });

      map.panTo(position);
      setDestination(marker);
      setValue('l_latitude', lat);
      setValue('l_longitude', lng);

      // Try to get address details for the coordinates
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          handleAddressChange(results[0], position);
        } else {
          handleAddressChange(null, position);
        }
      });

      return;
    }

    // Otherwise, use Google Places Autocomplete
    const autocomplete = new window.google.maps.places.Autocomplete(input);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (!place.geometry || !map) return;

      const position = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };

      if (destination) destination.setMap(null);

      const marker = new window.google.maps.Marker({
        position,
        map,
      });

      setDestination(marker);
      setValue('l_latitude', position.lat);
      setValue('l_longitude', position.lng);
      setSearchValue(`${position.lat}, ${position.lng}`);
      handleAddressChange(place, position);
    });
  };

  const handleAddressChange = (place, position) => {
    if (place) {
      const dir = place.formatted_address;
      setValue('l_address', dir);
      const cit = checkLongName(place, 2) ? place.address_components[2].long_name : null;
      setValue('l_city', cit);
      const stat = checkLongName(place, 4) ? place.address_components[4].long_name : null;
      setValue('l_state', null ? null : stat);
      const zip = checkLongName(place, 6) ? place.address_components[6].long_name : null;
      setValue('l_zip', null ? null : zip);

      handleMaps({ latLng: { lat: position.lat, lng: position.lng }, dir, cit, stat, zip });
    } else {
      setValue('l_address', '');
      setValue('l_city', '');
      setValue('l_state', '');
      setValue('l_zip', '');
      handleMaps({ latLng: position, dir: '', cit: '', stat: '', zip: '' });
    }
  };

  return (
    <div className='App'>
      <TextField
        id='autocomplete-input'
        label='Search Address or Coordinates (latitude , longitude)'
        variant='outlined'
        fullWidth
        value={searchValue}
        onChange={search}
        InputLabelProps={{ shrink: true }}
        style={{ marginBottom: '16px' }}
      />
      <div ref={mapRef} style={{ height: 512, width: '100%' }}></div>
    </div>
  );
};

export default DestinationMap;
